import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageCircle, Link2, Unlink, RefreshCw, Send, ShieldAlert, Users, Paperclip, Loader } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { whatsappApi } from '../../utils/api';

const fmtHeure = (ts) => { try { return new Date(ts * 1000).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

export default function WhatsAppPage() {
  const { structures, selectedStructure } = useApp();
  const [selId, setSelId] = useState(selectedStructure || (structures[0] && structures[0].id) || null);
  const [status, setStatus] = useState('hors_ligne');
  const [qr, setQr] = useState(null);
  const [chats, setChats] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);
  const msgEnd = useRef(null);
  const msgListRef = useRef(null);
  const prevCount = useRef(0);
  const prevActiveJid = useRef(null);
  const fileRef = useRef(null);
  const [sendingPJ, setSendingPJ] = useState(false);

  useEffect(() => { if (!selId && structures.length) setSelId(structures[0].id); }, [structures, selId]);

  const loadStatus = useCallback(async () => {
    if (!selId) return;
    try { const { data } = await whatsappApi.status(selId); setStatus(data.status); setQr(data.qr || null); }
    catch {}
  }, [selId]);

  const loadChats = useCallback(async () => {
    if (!selId) return;
    try { const { data } = await whatsappApi.chats(selId); setChats(data || []); } catch {}
  }, [selId]);

  const loadMessages = useCallback(async () => {
    if (!selId || !active) return;
    try { const { data } = await whatsappApi.messages(selId, active.jid); setMessages(data || []); } catch {}
  }, [selId, active]);

  // Poll statut + données
  useEffect(() => {
    loadStatus(); loadChats();
    pollRef.current = setInterval(() => { loadStatus(); loadChats(); }, 4000);
    return () => clearInterval(pollRef.current);
  }, [loadStatus, loadChats]);

  useEffect(() => { loadMessages(); }, [loadMessages]);
  useEffect(() => {
    if (!active) return;
    const t = setInterval(loadMessages, 4000);
    return () => clearInterval(t);
  }, [active, loadMessages]);
  // Auto-défilement SEULEMENT à l'ouverture d'une conversation ou à l'arrivée d'un nouveau message
  // si on est déjà en bas — et dans le conteneur, pas la page (sinon ça « remonte » à chaque relevé).
  useEffect(() => {
    const el = msgListRef.current;
    if (!el) return;
    const chatChanged = (active?.jid || null) !== prevActiveJid.current;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 140;
    if (chatChanged || (messages.length > prevCount.current && nearBottom)) {
      el.scrollTop = el.scrollHeight;
    }
    prevCount.current = messages.length;
    prevActiveJid.current = active?.jid || null;
  }, [messages, active]);

  const connecter = async () => { setBusy(true); try { const { data } = await whatsappApi.connect(selId); setStatus(data.status); setQr(data.qr || null); } catch {} setBusy(false); };
  const deconnecter = async () => { if (!window.confirm('Déconnecter WhatsApp de cette structure ?')) return; setBusy(true); try { await whatsappApi.disconnect(selId); setStatus('hors_ligne'); setQr(null); setChats([]); setActive(null); setMessages([]); } catch {} setBusy(false); };
  const envoyer = async () => { if (!reply.trim() || !active) return; const t = reply; setReply(''); try { await whatsappApi.send(selId, active.jid, t); loadMessages(); } catch (e) { alert('Envoi impossible : ' + (e.response?.data?.error || e.message)); setReply(t); } };
  const envoyerPJ = async (file) => {
    if (!file || !active) return;
    const caption = reply; setSendingPJ(true);
    try { await whatsappApi.sendMedia(selId, active.jid, file, caption); setReply(''); loadMessages(); }
    catch (e) { alert('Envoi de la pièce jointe impossible : ' + (e.response?.data?.error || e.message)); }
    finally { setSendingPJ(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const changeStructure = (id) => { setSelId(id); setStatus('hors_ligne'); setQr(null); setChats([]); setActive(null); setMessages([]); };

  const connected = status === 'connected';

  return (
    <div className="max-w-6xl">
      <div className="mb-4">
        <h1 className="font-serif text-[28px] text-navy-900 flex items-center gap-2"><MessageCircle size={24} className="text-emerald-600" /> Messagerie WhatsApp</h1>
        <div className="w-12 h-0.5 bg-gold-400 rounded-full mt-2 mb-2" />
        <p className="text-navy-400 text-[13.5px]">Consultation des conversations WhatsApp <strong>par structure</strong> (appareil connecté). Le téléphone de la crèche garde tout et continue de fonctionner normalement.</p>
      </div>

      {/* Sélecteur structure */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {structures.map(s => (
          <button key={s.id} onClick={() => changeStructure(s.id)}
            className={`px-4 py-2 rounded-xl text-[14px] border transition-colors ${String(selId) === String(s.id) ? 'bg-navy-800 text-white border-navy-800' : 'bg-white text-navy-600 border-cream-300 hover:border-navy-300'}`}>
            {s.nom}
          </button>
        ))}
        <span className={`ml-auto text-[12px] px-2.5 py-1 rounded-full font-medium ${connected ? 'bg-emerald-100 text-emerald-700' : status === 'qr' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
          {connected ? '● Connecté' : status === 'qr' ? 'En attente de scan' : status === 'reconnexion' ? 'Reconnexion…' : status === 'connecting' ? 'Connexion…' : 'Hors ligne'}
        </span>
      </div>

      {!connected ? (
        <div className="apple-card p-6 max-w-xl">
          {status === 'qr' && qr ? (
            <div className="text-center">
              <p className="text-[14px] text-navy-700 mb-3 font-medium">Scanne ce QR depuis le téléphone de la crèche :</p>
              <p className="text-[12.5px] text-navy-400 mb-3">WhatsApp → <strong>Réglages</strong> → <strong>Appareils connectés</strong> → <strong>Connecter un appareil</strong></p>
              <img src={qr} alt="QR WhatsApp" className="mx-auto w-56 h-56 rounded-xl border border-cream-300" />
              <p className="text-[11.5px] text-navy-300 mt-3">Le QR se rafraîchit tout seul. Une fois scanné, les conversations apparaîtront ici.</p>
            </div>
          ) : (
            <div className="text-center py-4">
              <MessageCircle size={40} className="mx-auto text-emerald-500 mb-3" />
              <p className="text-[14px] text-navy-700 mb-1">Connecter WhatsApp pour <strong>{structures.find(s => String(s.id) === String(selId))?.nom || 'cette structure'}</strong></p>
              <p className="text-[12.5px] text-navy-400 mb-4">Un QR code s'affichera, à scanner depuis le téléphone de la crèche.</p>
              <button onClick={connecter} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 text-white text-[14px] font-medium hover:bg-emerald-700 disabled:opacity-50">
                {busy ? <RefreshCw size={16} className="animate-spin" /> : <Link2 size={16} />} Générer le QR de connexion
              </button>
            </div>
          )}
          <div className="mt-5 pt-4 border-t border-cream-200 flex items-start gap-2 text-[11.5px] text-navy-400">
            <ShieldAlert size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <span>Connexion « appareil lié » (comme WhatsApp Web). Usage <strong>consultatif</strong> conseillé. Les conversations rapatriées sont des données personnelles — pensez à en informer les familles (RGPD).</span>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-[300px_1fr] gap-4">
          {/* Liste conversations */}
          <div className="apple-card p-2 h-max max-h-[70vh] overflow-y-auto">
            <div className="flex items-center justify-between px-2 py-1.5">
              <span className="text-[11px] uppercase tracking-wide text-navy-400">{chats.length} conversation{chats.length > 1 ? 's' : ''}</span>
              <button onClick={deconnecter} title="Déconnecter" className="text-navy-300 hover:text-red-500"><Unlink size={14} /></button>
            </div>
            {chats.length === 0 && <div className="text-[12.5px] text-navy-300 px-2 py-6 text-center">Aucune conversation pour l'instant.<br />Les nouveaux messages apparaîtront ici.</div>}
            {chats.map(c => (
              <button key={c.jid} onClick={() => setActive(c)}
                className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors ${active && active.jid === c.jid ? 'bg-emerald-50' : 'hover:bg-cream-100'}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-[13.5px] text-navy-900 truncate flex items-center gap-1.5">{c.groupe && <Users size={12} className="text-navy-300" />}{c.nom}</span>
                  <span className="text-[10px] text-navy-300 flex-shrink-0">{fmtHeure(c.ts).slice(0, 5)}</span>
                </div>
                <div className="text-[12px] text-navy-400 truncate">{c.dernier_de_moi ? 'Vous : ' : ''}{c.dernier}</div>
              </button>
            ))}
          </div>

          {/* Conversation */}
          <div className="apple-card flex flex-col h-[70vh]">
            {!active ? (
              <div className="flex-1 flex items-center justify-center text-navy-300 text-[13px]">Sélectionnez une conversation</div>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-cream-200 font-medium text-navy-900 flex items-center gap-2">
                  {active.groupe && <Users size={15} className="text-navy-300" />}{active.nom}{active.numero && <span className="text-[12px] text-navy-300 font-normal">· {active.numero}</span>}
                </div>
                <div ref={msgListRef} className="flex-1 overflow-y-auto p-4 space-y-1.5 bg-cream-50/40">
                  {messages.map(m => (
                    <div key={m.id} className={`flex ${m.from_me ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] rounded-2xl px-3 py-1.5 text-[13px] ${m.from_me ? 'bg-emerald-100 text-navy-900' : 'bg-white border border-cream-300 text-navy-800'}`}>
                        {active.groupe && !m.from_me && m.contact_nom && <div className="text-[10.5px] font-semibold text-emerald-700">{m.contact_nom}</div>}
                        <div className="whitespace-pre-wrap break-words">{m.body}</div>
                        <div className="text-[9.5px] text-navy-300 text-right mt-0.5">{fmtHeure(m.ts)}</div>
                      </div>
                    </div>
                  ))}
                  <div ref={msgEnd} />
                </div>
                <div className="p-2.5 border-t border-cream-200 flex items-center gap-2">
                  <input ref={fileRef} type="file" className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.gif,.webp,.heic,.mp4,.mp3,.m4a,.ogg"
                    onChange={e => { const f = e.target.files && e.target.files[0]; if (f) envoyerPJ(f); }} />
                  <button type="button" onClick={() => fileRef.current && fileRef.current.click()} disabled={sendingPJ}
                    title="Joindre un document ou une photo"
                    className="p-2.5 rounded-xl text-navy-400 hover:bg-cream-100 hover:text-navy-700 disabled:opacity-40">
                    {sendingPJ ? <Loader size={16} className="animate-spin" /> : <Paperclip size={16} />}
                  </button>
                  <input value={reply} onChange={e => setReply(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') envoyer(); }}
                    placeholder="Répondre ou légender une pièce jointe… (optionnel)" className="form-input rounded-xl flex-1 text-[13px]" />
                  <button onClick={envoyer} disabled={!reply.trim()} className="p-2.5 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"><Send size={16} /></button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
